/**
 * lib/commentTemplates.ts
 * ------------------------------------------------------------------
 * The approved-comment-template engine. Faithful to Al-Nur's model
 * (functions__reportsAI.js getMarkCategory + builtInFallbacks,
 * components__CommentTemplatesManager.jsx data shape).
 *
 * Mark -> category (same thresholds as Al-Nur):
 *   >=90 exceptional, >=80 excellent, >=70 good,
 *   >=60 satisfactory, >=50 needsImprovement, else concern
 *
 * Template store (Firestore collection COLLECTIONS.commentTemplates):
 *   doc 'subjectTemplatesByForm'  { [formLevel]:{ [subject]:{ [cat]:string[] }}}
 *   doc 'subjectTemplatesGeneral' { [subject]:{ [cat]:string[] }}
 *   doc 'classTeacherTemplates'   { attire:[], conduct:[], progress:{pass:[],fail:[]} }
 *
 * Templates use a {name} placeholder. If no approved template
 * exists for a (form,subject,category), we fall back to the
 * built-in bank below, so the feature is useful with ZERO seeded
 * data — exactly Al-Nur's documented behaviour.
 * ------------------------------------------------------------------
 */

export type MarkCategory =
  | 'exceptional'
  | 'excellent'
  | 'good'
  | 'satisfactory'
  | 'needsImprovement'
  | 'concern';

export const MARK_CATEGORIES: MarkCategory[] = [
  'exceptional',
  'excellent',
  'good',
  'satisfactory',
  'needsImprovement',
  'concern',
];

export const CATEGORY_LABEL: Record<MarkCategory, string> = {
  exceptional: 'Exceptional (90+)',
  excellent: 'Excellent (80–89)',
  good: 'Good (70–79)',
  satisfactory: 'Satisfactory (60–69)',
  needsImprovement: 'Needs improvement (50–59)',
  concern: 'Concern (<50)',
};

/** Same thresholds as Al-Nur's getMarkCategory. */
export function getMarkCategory(mark: number): MarkCategory {
  if (mark >= 90) return 'exceptional';
  if (mark >= 80) return 'excellent';
  if (mark >= 70) return 'good';
  if (mark >= 60) return 'satisfactory';
  if (mark >= 50) return 'needsImprovement';
  return 'concern';
}

/** Built-in fallback bank (used when no approved template exists). */
export const BUILT_IN_FALLBACKS: Record<MarkCategory, string[]> = {
  exceptional: [
    '{name} has achieved outstanding results and demonstrates mastery of the subject.',
    '{name} continues to set the standard with exceptional performance; {he} should be very proud of this achievement.',
    '{name} has produced excellent work and clearly enjoys the subject; {he} can aim even higher next term.',
  ],
  excellent: [
    '{name} has performed very well and shows strong understanding throughout.',
    '{name} consistently produces high-quality work and should be commended for {his} effort.',
    '{name} has had an excellent term and is encouraged to maintain {his} high standard.',
  ],
  good: [
    '{name} has made good progress and demonstrates a sound grasp of the subject.',
    '{name} is working well and should continue to build on {his} solid foundation.',
    '{name} has shown good understanding; {he} should keep up this steady, reliable effort.',
  ],
  satisfactory: [
    '{name} is working at the expected level and should aim to push further.',
    '{name} has a satisfactory grasp of the subject; more practice will lift {his} results.',
    '{name} is making steady progress and {he} should aim to be more consistent.',
  ],
  needsImprovement: [
    '{name} is working below the expected level and needs to apply more effort.',
    '{name} should commit to regular revision to consolidate {his} understanding.',
    '{name} has the potential to improve and {he} must work more consistently.',
  ],
  concern: [
    '{name} is performing well below the expected standard and needs urgent support.',
    '{name} must seek additional help and commit to a serious study routine.',
    '{name} requires close monitoring; {he} must follow a structured revision plan to make progress.',
  ],
};

/** Student gender as stored on the record ('' = unknown → neutral they/their). */
export type Gender = 'M' | 'F' | '';

/** Pronoun set for the {he}/{him}/{his} placeholders (and capitalised forms).
 *  Unknown gender resolves to singular-they; templates should pair these with
 *  MODAL verbs ("{he} should…", "{he} can…") so agreement holds across he/she/they. */
function pronounsFor(g: Gender): { he: string; He: string; him: string; his: string; His: string } {
  if (g === 'M') return { he: 'he', He: 'He', him: 'him', his: 'his', His: 'His' };
  if (g === 'F') return { he: 'she', He: 'She', him: 'her', his: 'her', His: 'Her' };
  return { he: 'they', He: 'They', him: 'them', his: 'their', His: 'Their' };
}

/** Replace {name} with the first name and the pronoun placeholders by gender. */
export function fillTemplate(tpl: string, studentName: string, gender: Gender = ''): string {
  const first = (studentName || '').trim().split(/\s+/)[0] || 'The student';
  const p = pronounsFor(gender);
  return tpl
    .replace(/\{name\}/g, first)
    .replace(/\{He\}/g, p.He)
    .replace(/\{he\}/g, p.he)
    .replace(/\{His\}/g, p.His)
    .replace(/\{his\}/g, p.his)
    .replace(/\{him\}/g, p.him);
}

/* ---- store shapes ------------------------------------------------ */
export interface SubjectTemplatesByForm {
  // formLevel -> subjectId -> category -> string[]
  [formLevel: string]: Record<
    string,
    Partial<Record<MarkCategory, string[]>>
  >;
}
export interface SubjectTemplatesGeneral {
  // subjectId -> category -> string[]
  [subjectId: string]: Partial<Record<MarkCategory, string[]>>;
}
export interface ClassTeacherTemplates {
  attire: string[];
  conduct: string[];
  progress: { pass: string[]; fail: string[] };
}

/**
 * Resolve approved templates for a (formLevel, subjectId, category),
 * preferring form-specific, then general, then built-in fallback.
 * Returns the list and where it came from (for UI honesty).
 */
export function resolveTemplates(
  category: MarkCategory,
  opts: {
    formLevel?: string;
    subjectId?: string;
    byForm?: SubjectTemplatesByForm;
    general?: SubjectTemplatesGeneral;
  },
): { templates: string[]; source: 'form' | 'general' | 'built-in' } {
  const { formLevel, subjectId, byForm, general } = opts;
  if (formLevel && subjectId && byForm) {
    const t = byForm[formLevel]?.[subjectId]?.[category];
    if (t && t.length) return { templates: t, source: 'form' };
  }
  if (subjectId && general) {
    const t = general[subjectId]?.[category];
    if (t && t.length) return { templates: t, source: 'general' };
  }
  return {
    templates: BUILT_IN_FALLBACKS[category],
    source: 'built-in',
  };
}

/**
 * Pick one suggestion for a student. Deterministic-ish: rotates by
 * a seed (e.g. existing-comment count) so repeated clicks vary.
 */
export function suggestComment(
  studentName: string,
  category: MarkCategory,
  pool: string[],
  seed = 0,
  gender: Gender = '',
): string {
  if (pool.length === 0) {
    return fillTemplate(BUILT_IN_FALLBACKS[category][0], studentName, gender);
  }
  const idx = Math.abs(seed) % pool.length;
  return fillTemplate(pool[idx], studentName, gender);
}
